import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import ReactCrop, { type PixelCrop, type PercentCrop } from "react-image-crop";
import { Flex, Button, Slider } from "@radix-ui/themes";
import { SwapSwitchIcon } from "@/assets/icons";
import { selectImageFile, compressImage } from "@/lib/image-utils";

export type ImageCropperHandle = {
  emitCroppedImage: () => Promise<void>;
  replaceImage: () => void;
};

type ImageCropperProps = {
  src: string;
  onComplete: (file: File) => void;
  onChangeImage: (dataUrl: string) => void;
  outputSize?: number;
  circular?: boolean;
};

export const ImageCropper = forwardRef<ImageCropperHandle, ImageCropperProps>(
  function ImageCropper(
    { src, onComplete, onChangeImage, outputSize = 1000, circular = false },
    ref,
  ) {
    const [crop, setCrop] = useState<PercentCrop | null>(null);
    const [lastPixelCrop, setLastPixelCrop] = useState<PixelCrop | null>(null);
    const [zoom, setZoom] = useState(1);
    const imgRef = useRef<HTMLImageElement | null>(null);

    const emitCroppedImage = useCallback(async () => {
      if (!imgRef.current || !lastPixelCrop) return;

      const image = imgRef.current;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      canvas.width = outputSize;
      canvas.height = outputSize;

      // Adjust for Zoom:
      const zoomedWidth = lastPixelCrop.width / zoom;
      const zoomedHeight = lastPixelCrop.height / zoom;

      // Center the source coordinates based on the zoom offset
      const offsetX = (lastPixelCrop.width - zoomedWidth) / 2;
      const offsetY = (lastPixelCrop.height - zoomedHeight) / 2;

      ctx.drawImage(
        image,
        (lastPixelCrop.x + offsetX) * scaleX,
        (lastPixelCrop.y + offsetY) * scaleY,
        zoomedWidth * scaleX,
        zoomedHeight * scaleY,
        0,
        0,
        outputSize,
        outputSize,
      );

      canvas.toBlob(
        async (blob) => {
          if (!blob) return;
          // Unique name per crop, never a fixed one: the API keys S3 objects
          // by the uploaded filename (creators/{id}/{type}/{name}), so a fixed
          // "image.jpg" overwrote the same key on every upload. The URL then
          // never changed, and the browser and CloudFront kept serving the
          // cached old picture — replacing an existing avatar looked like it
          // did nothing. A fresh name makes a fresh URL, which no cache holds.
          const file = new File([blob], `image-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          const compressed = await compressImage(file);
          onComplete(compressed);
        },
        "image/jpeg",
        0.9,
      );
    }, [lastPixelCrop, onComplete, outputSize, zoom]);

    const replaceImage = useCallback(() => {
      selectImageFile((dataUrl) => {
        onChangeImage(dataUrl);
        setZoom(1);
      });
    }, [onChangeImage]);

    useImperativeHandle(ref, () => ({
      emitCroppedImage,
      replaceImage,
    }));

    function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
      const img = e.currentTarget;
      imgRef.current = img;

      // rendered size (after object-contain)
      const rw = img.clientWidth;
      const rh = img.clientHeight;

      const side = Math.min(rw, rh);

      const cropPercent: PercentCrop = {
        unit: "%",
        width: (side / rw) * 100,
        height: (side / rh) * 100,
        x: ((rw - side) / 2 / rw) * 100,
        y: ((rh - side) / 2 / rh) * 100,
      };

      setCrop(cropPercent);
    }

    return (
      <>
        <Flex
          justify="center"
          align="center"
          width={{ initial: "full", sm: "540px" }}
          className="relative overflow-hidden bg-background-neutral-quarternary rounded-md my-[12px] h-[400px]"
        >
          <ReactCrop
            crop={crop ?? undefined}
            onChange={(_px, percentCrop) => {
              setCrop(percentCrop);
            }}
            onComplete={(pixelCrop: PixelCrop) => setLastPixelCrop(pixelCrop)}
            aspect={1}
            circularCrop={circular}
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          >
            <img
              ref={imgRef}
              src={src}
              alt="Crop source"
              onLoad={handleImageLoad}
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
                transition: "transform 0.2s ease",
              }}
            />
          </ReactCrop>
        </Flex>

        <Flex
          direction={{ initial: "column-reverse", sm: "row" }}
          align="center"
          justify="between"
          className="my-2 px-2"
        >
          <Button
            variant="ghost"
            className="hidden sm:inline-flex"
            onClick={replaceImage}
          >
            <SwapSwitchIcon />
            Replace image
          </Button>
          <Flex align="center" gap="2">
            <Button
              type="button"
              radius="full"
              variant="ghost"
              className="w-[24px] h-[24px] text-[20px] font-semibold text-black flex items-center justify-center hover:bg-gray-300 hover:text-blue-600 mr-[1px]"
              onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
            >
              −
            </Button>

            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.1}
              onValueChange={([v]) => setZoom(v)}
              className="w-[200px]"
            />

            <Button
              type="button"
              radius="full"
              variant="ghost"
              className="w-[24px] h-[24px] text-[20px] font-semibold text-black flex items-center justify-center hover:bg-gray-300 hover:text-blue-600 ml-[1px]"
              onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
            >
              +
            </Button>
          </Flex>
        </Flex>
      </>
    );
  },
);
